import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { LinkButton } from '@eika/button';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { Select } from '@eika/select';

import { GjeldAksjonWeb, GjeldKildeWeb, GjeldTypeWeb, StatusWeb, ValgtWeb } from '../../generated-types';
import { useTranslation } from '../../translations';
import { GjeldFormData } from './gjeld';

export const newGjeld = {
    aksjon: GjeldAksjonWeb.IKKE_VALGT,
    gjeldId: '',
    kilde: GjeldKildeWeb.KUNDE,
    kredittramme: undefined,
    laangiver: '',
    restgjeld: undefined,
    sikret: false,
    status: StatusWeb.CONFIRMED,
    gjeldType: GjeldTypeWeb.IKKE_VALGT,
    skalInnfrisValg: ValgtWeb.IKKE_VALGT,
    maanedligeFellesutgifter: null,
    skalViseFellesutgifter: false,
    pantebeloep: null,
    registrertDato: null,
    registreringsnummer: null,
};

interface Props {
    index: number;
    onClickSlett: (index: number) => void;
}

const GjeldKunde: FC<Props> = ({ index, onClickSlett }) => {
    const { gjeld, validationMessage, button } = useTranslation();
    const {
        register,
        control,
        formState: { errors },
    } = useFormContext<GjeldFormData>();

    return (
        <div className="form-box-seagreen row-gap margin-bottom-small">
            <Input
                label={gjeld.labelBankensNavn}
                type="text"
                error={errors.gjeld?.[index]?.laangiver?.message}
                {...register(`gjeld.${index}.laangiver` as const, {
                    required: `${validationMessage.requiredValue}`,
                })}
            />
            <Select
                label={gjeld.labelTypeLaan}
                values={[
                    {
                        text: gjeld.selectValueDefault,
                        value: GjeldTypeWeb.IKKE_VALGT,
                        disabled: true,
                    },
                    {
                        text: gjeld.selectValueBillaan,
                        value: GjeldTypeWeb.BILLAAN,
                    },
                    {
                        text: gjeld.selectValueStudielaan,
                        value: GjeldTypeWeb.STUDIELAAN,
                    },
                    {
                        text: gjeld.selectValueBoliglaan,
                        value: GjeldTypeWeb.BOLIGLAAN,
                    },
                    {
                        text: gjeld.selectValueAnnet,
                        value: GjeldTypeWeb.ANNET,
                    },
                ]}
                error={errors.gjeld?.[index]?.gjeldType?.type === 'gyldigValg' && `${validationMessage.requiredChoose}`}
                {...register(`gjeld.${index}.gjeldType` as const, {
                    validate: { gyldigValg: (value) => value !== GjeldTypeWeb.IKKE_VALGT },
                })}
            />
            <Controller
                control={control}
                name={`gjeld.${index}.restgjeld` as const}
                rules={{
                    required: `${validationMessage.requiredValue}`,
                }}
                render={({ field: { onChange, value } }) => (
                    <CurrencyInput
                        name={`gjeld.${index}.restgjeld` as const}
                        placeholder=""
                        className="max-width-200"
                        type="tel"
                        label={gjeld.labelRestGjeld}
                        value={value}
                        onValueChange={(v) => onChange(v.value)}
                        error={errors.gjeld?.[index]?.restgjeld?.message}
                    />
                )}
            />
            <div className="column">
                <LinkButton
                    icon={IconTypes.SLETT_24}
                    data-track-id="kreditt-boliglaan-gjeld-slett-ikon"
                    data-track-linkname="fjern"
                    onClick={() => onClickSlett(index)}
                >
                    {button.fjern}
                </LinkButton>
            </div>
        </div>
    );
};

export default GjeldKunde;
